import React, { useEffect, useRef, useCallback, useState } from 'react';

const THRESHOLD    = 80;
const SCROLL_ZONE  = 60;  // px from top/bottom edge to trigger auto-scroll
const SCROLL_SPEED = 8;   // px per frame

export default function Transcript({ lines, theme }) {
  const boxRef      = useRef(null);
  const bottomRef   = useRef(null);
  const isAtBottom  = useRef(true);
  const rafRef      = useRef(null);
  const dragging    = useRef(false);

  const [popup, setPopup]   = useState(null);
  const [copied, setCopied] = useState(false);

  // ── auto-scroll on new lines ───────────────────────────────────────────────
  const checkBottom = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    isAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= THRESHOLD;
  }, []);

  useEffect(() => {
    if (isAtBottom.current) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  // ── pointer capture: lock all events to the box ───────────────────────────
  const stopScroll = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  const handlePointerDown = useCallback((e) => {
    // only capture touch input (pointerId > 0 on touch)
    if (e.pointerType !== 'touch') return;
    dragging.current = true;
    setPopup(null);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!dragging.current || e.pointerType !== 'touch') return;
    const box  = boxRef.current;
    if (!box) return;

    const rect = box.getBoundingClientRect();
    const y    = e.clientY;

    stopScroll();

    // auto-scroll when finger is near top or bottom edge
    if (y < rect.top + SCROLL_ZONE) {
      const speed = SCROLL_SPEED * ((rect.top + SCROLL_ZONE - y) / SCROLL_ZONE);
      const tick = () => {
        box.scrollTop -= speed;
        checkBottom();
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } else if (y > rect.bottom - SCROLL_ZONE) {
      const speed = SCROLL_SPEED * ((y - (rect.bottom - SCROLL_ZONE)) / SCROLL_ZONE);
      const tick = () => {
        box.scrollTop += speed;
        checkBottom();
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [checkBottom, stopScroll]);

  const handlePointerUp = useCallback((e) => {
    if (e.pointerType !== 'touch') return;
    dragging.current = false;
    stopScroll();
    checkBottom();

    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.toString().trim()) { setPopup(null); return; }
      const box = boxRef.current;
      if (!box || !box.contains(sel.anchorNode)) { setPopup(null); return; }

      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setPopup({ x: rect.left + rect.width / 2, y: rect.top - 12, text: sel.toString().trim() });
      setCopied(false);
    }, 120);
  }, [checkBottom, stopScroll]);

  // mouse support (desktop)
  const handleMouseUp = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) { setPopup(null); return; }
    const box = boxRef.current;
    if (!box || !box.contains(sel.anchorNode)) { setPopup(null); return; }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setPopup({ x: rect.left + rect.width / 2, y: rect.top - 12, text: sel.toString().trim() });
    setCopied(false);
  }, []);

  // dismiss on tap outside popup
  const handlePointerDownDismiss = useCallback((e) => {
    if (popup && !e.target.closest('[data-copy-btn]')) {
      setPopup(null);
      window.getSelection()?.removeAllRanges();
    }
  }, [popup]);

  async function handleCopy() {
    if (!popup) return;
    try {
      await navigator.clipboard.writeText(popup.text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = popup.text;
      Object.assign(ta.style, { position: 'fixed', opacity: 0 });
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => { setPopup(null); window.getSelection()?.removeAllRanges(); }, 900);
  }

  useEffect(() => stopScroll, [stopScroll]);

  return (
    <>
      {popup && (
        <div data-copy-btn onClick={handleCopy} style={popupStyle(popup, copied, theme)}>
          {copied ? '✓ Copied' : '⎘ Copy'}
        </div>
      )}

      <div
        ref={boxRef}
        onScroll={checkBottom}
        onPointerDown={(e) => { handlePointerDownDismiss(e); handlePointerDown(e); }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onMouseUp={handleMouseUp}
        style={boxStyle(theme)}
      >
        {lines.length === 0
          ? <p style={{ color: theme.textMuted, fontStyle: 'italic', textAlign: 'center', marginTop: 80 }}>
              Transcript will appear here…
            </p>
          : lines.map((l, i) => (
              <div key={l.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '6px 0', borderBottom: `1px solid ${theme.rowDivider}` }}>
                <span style={{ fontSize: 11, color: theme.indexColor, minWidth: 20, paddingTop: 3, userSelect: 'none' }}>{i + 1}</span>
                <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: theme.textLine }}>{l.text}</p>
              </div>
            ))
        }
        <div ref={bottomRef} />
      </div>
    </>
  );
}

function popupStyle({ x, y }, copied, theme) {
  return {
    position:   'fixed',
    left:       x,
    top:        y,
    transform:  'translate(-50%, -100%)',
    background: copied ? '#16a34a' : theme.text,
    color:      theme.page,
    fontSize:   13,
    fontWeight: 600,
    padding:    '6px 14px',
    borderRadius: 20,
    boxShadow:  '0 4px 16px rgba(0,0,0,0.25)',
    cursor:     'pointer',
    zIndex:     9999,
    whiteSpace: 'nowrap',
    userSelect: 'none',
    transition: 'background 0.2s',
  };
}

function boxStyle(theme) {
  return {
    background:              theme.transcriptBg,
    border:                  `1.5px solid ${theme.border}`,
    borderRadius:            12,
    padding:                 '16px 20px',
    flex:                    1,
    overflowY:               'auto',
    WebkitOverflowScrolling: 'touch',
    touchAction:             'pan-y',   // allow vertical scroll but capture pointer
  };
}
